import { Facebook, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import visaIcon from "@/assets/payment-visa.svg";
import mastercardIcon from "@/assets/payment-mastercard.svg";
import paypalIcon from "@/assets/payment-paypal.svg";
import dinersIcon from "@/assets/payment-diners.svg";
import amexIcon from "@/assets/payment-amex.svg";
import logoImage from "@/assets/logo-alice.png";
import { useCookieConsentContext } from "@/contexts/CookieConsentContext";

interface MenuItem {
  title: string;
  links: {
    text: string;
    url: string;
  }[];
}

interface Footer2Props {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  tagline?: string;
  menuItems?: MenuItem[];
  copyright?: string;
}

// Helper to check if URL is external
const isExternalLink = (url: string) => {
  return url.startsWith("http") || url.startsWith("mailto:") || url.startsWith("tel:") || url.startsWith("#");
};

const Footer2 = ({
  logo = {
    src: logoImage,
    alt: "אליס תכשיטים ושעונים",
    title: "Alice Jewelry",
    url: "/",
  },
  tagline = "תכשיטים ושעונים משנת 1984",
  menuItems = [
    {
      title: "קישורים חשובים",
      links: [
        { text: "מדיניות פרטיות", url: "/privacy" },
        { text: "תנאי שימוש", url: "/terms" },
        { text: "מדיניות משלוחים", url: "/shipping" },
        { text: "מדיניות החזרות", url: "/returns" },
        { text: "הצהרת נגישות", url: "/accessibility" },
        { text: "מדיניות עוגיות", url: "/cookie-policy" },
        { text: "ניהול עוגיות", url: "__cookie_preferences__" },
      ],
    },
    {
      title: "שירות לקוחות",
      links: [
        { text: "שאלות נפוצות", url: "/faq" },
        { text: "מדריך מידות", url: "/ring-size-guide" },
        { text: "Alice Trade-In", url: "/trade-in" },
        { text: "צור קשר", url: "/contact" },
        { text: "מעקב אחרי הזמנה", url: "/track-order" },
      ],
    },
    {
      title: "יצירת קשר",
      links: [
        { text: "טלפון: 079-5575576", url: "tel:079-5575576" },
        { text: "Whatsapp: 054-5267564", url: "https://wa.me/972545267564" },
        { text: "info@alicejewelry.net", url: "mailto:info@alicejewelry.net" },
        {
          text: "כתובת: הרצל 58, באר שבע",
          url: "https://www.google.com/maps/dir//%D7%90%D7%9C%D7%99%D7%A1+%7C+%D7%AA%D7%9B%D7%A9%D7%99%D7%98%D7%99%D7%9D+%D7%95%D7%A9%D7%A2%D7%95%D7%A0%D7%99%D7%9D+%D7%94%D7%A8%D7%A6%D7%9C+58+%D7%91%D7%90%D7%A8+%D7%A9%D7%91%D7%A2%E2%80%AD/@31.2411707,34.7895052,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x150266113eb89b75:0xa6850d035d15c6e8",
        },
      ],
    },
    {
      title: "רשת חברתית",
      links: [
        { text: "פייסבוק", url: "https://www.facebook.com/alicejewelryil" },
        { text: "אינסטגרם", url: "https://www.instagram.com/alice_jewelry.il/" },
        { text: "טיקטוק", url: "https://tiktok.com/@alisjewelry" },
        { text: "דוא״ל", url: "mailto:info@alicejewelry.net" },
      ],
    },
  ],
  copyright = "© אליס | תכשיטים ושעונים 2025",
}: Footer2Props) => {
  const { openPreferences } = useCookieConsentContext();
  return (
    <section className="py-16 md:py-20 bg-muted/30 border-t">
      <div className="container">
        <footer>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8" dir="rtl">
            <div className="col-span-1 sm:col-span-2 mb-4 lg:mb-0 flex flex-col items-start">
              <div className="flex items-center gap-3">
                <Link to={logo.url} className="flex items-center gap-3">
                  <img src={logo.src} alt={logo.alt} title={logo.title} className="h-12 w-auto" />
                </Link>
              </div>
              <p className="mt-4 font-medium text-muted-foreground text-sm text-left">{tagline}</p>
              <div className="flex gap-3 mt-6">
                <a
                  href="mailto:info@alicejewelry.net"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  aria-label="דוא״ל"
                >
                  <Mail className="h-5 w-5" />
                </a>
                <a
                  href="https://www.facebook.com/alicejewelryil"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="פייסבוק"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/alice_jewelry.il/"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="אינסטגרם"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://tiktok.com/@alisjewelry"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="טיקטוק"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
                <a
                  href="https://apps.apple.com/us/app/alice-jewelry/id1544496428"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="App Store"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <path d="M30.62 20.419c-0.239-0.321-0.573-0.559-0.96-0.672l-0.013-0.003c-0.165-0.047-0.357-0.079-0.555-0.088l-0.006-0c-0.21-0.012-0.419-0.011-0.629-0.011h-4.15l-5.485-9.768c-0.697 0.73-1.18 1.671-1.343 2.718l-0.004 0.029c-0.044 0.268-0.069 0.578-0.069 0.893 0 1.050 0.279 2.035 0.766 2.885l-0.015-0.028 6.591 11.738c0.105 0.187 0.209 0.374 0.324 0.554 0.107 0.172 0.224 0.32 0.355 0.456l-0.001-0.001c0.275 0.28 0.639 0.471 1.046 0.528l0.010 0.001c0.070 0.010 0.152 0.015 0.234 0.015 0.339 0 0.655-0.094 0.925-0.257l-0.008 0.005c0.349-0.211 0.618-0.523 0.771-0.894l0.005-0.012c0.090-0.215 0.142-0.466 0.142-0.728 0-0.169-0.022-0.334-0.062-0.49l0.003 0.014c-0.055-0.208-0.125-0.388-0.213-0.558l0.007 0.015c-0.094-0.193-0.2-0.379-0.305-0.565l-1.52-2.707h1.994c0.21 0 0.419 0.001 0.628-0.011 0.203-0.009 0.395-0.041 0.578-0.092l-0.018 0.004c0.4-0.117 0.735-0.355 0.97-0.671l0.003-0.005c0.235-0.316 0.376-0.714 0.376-1.144s-0.141-0.829-0.38-1.15l0.004 0.005zM4.894 24.175l-0.99 1.875c-0.102 0.194-0.205 0.387-0.298 0.588-0.079 0.161-0.148 0.349-0.197 0.544l-0.004 0.02c-0.037 0.149-0.058 0.32-0.058 0.495 0 0.272 0.051 0.533 0.144 0.772l-0.005-0.015c0.15 0.395 0.414 0.719 0.751 0.939l0.007 0.005c0.254 0.165 0.564 0.263 0.898 0.263 0.081 0 0.16-0.006 0.237-0.017l-0.009 0.001c0.412-0.063 0.769-0.262 1.031-0.549l0.001-0.001c0.127-0.139 0.242-0.294 0.339-0.46l0.007-0.013c0.113-0.188 0.214-0.382 0.317-0.576l1.436-2.72c-0.552-0.82-1.476-1.352-2.525-1.352-0.389 0-0.761 0.073-1.103 0.207l0.021-0.007zM12.012 19.648l7.636-13.792c0.105-0.189 0.21-0.377 0.305-0.573 0.081-0.157 0.151-0.34 0.201-0.531l0.004-0.019c0.038-0.145 0.059-0.311 0.059-0.483 0-0.266-0.052-0.519-0.147-0.751l0.005 0.013c-0.156-0.388-0.426-0.704-0.767-0.914l-0.008-0.005c-0.262-0.161-0.578-0.256-0.917-0.256-0.082 0-0.163 0.006-0.243 0.017l0.009-0.001c-0.417 0.059-0.782 0.253-1.055 0.535l-0 0.001c-0.13 0.136-0.247 0.287-0.347 0.449l-0.007 0.012c-0.115 0.183-0.219 0.372-0.324 0.561l-0.483 0.872-0.483-0.872c-0.105-0.189-0.208-0.379-0.323-0.561-0.107-0.174-0.224-0.325-0.355-0.462l0.001 0.001c-0.273-0.283-0.637-0.477-1.045-0.535l-0.010-0.001c-0.070-0.010-0.151-0.015-0.233-0.015-0.339 0-0.656 0.095-0.925 0.26l0.008-0.004c-0.349 0.215-0.619 0.531-0.771 0.906l-0.005 0.013c-0.090 0.219-0.142 0.472-0.142 0.738 0 0.171 0.022 0.338 0.062 0.496l-0.003-0.014c0.055 0.21 0.125 0.393 0.213 0.566l-0.007-0.016c0.094 0.195 0.2 0.384 0.305 0.573l1.56 2.817-6.076 10.975h-4.164c-0.209 0-0.419-0.001-0.628 0.012-0.203 0.009-0.395 0.041-0.578 0.094l0.018-0.004c-0.401 0.119-0.735 0.36-0.969 0.68l-0.004 0.005c-0.235 0.321-0.376 0.724-0.376 1.159s0.141 0.838 0.38 1.165l-0.004-0.006c0.237 0.325 0.571 0.566 0.959 0.681l0.013 0.003c0.165 0.048 0.357 0.080 0.554 0.089l0.006 0c0.209 0.012 0.419 0.011 0.628 0.011h16.367c0.045-0.093 0.087-0.205 0.121-0.321l0.004-0.016c0.078-0.243 0.123-0.523 0.123-0.813 0-1.487-1.185-2.698-2.662-2.74l-0.004-0z" />
                  </svg>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=co.median.android.nwbaxj"
                  className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-110"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Play"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.22 0c-.03.093-.06.185-.06.308v23.229c0 .217.061.34.184.463l11.415-12.093L1.22 0zm12.309 12.708l2.951 3.045-4.213 2.4s-5.355 3.044-8.308 4.739l9.57-10.184zm.801-.831l3.166 3.292c.496-.276 4.371-2.492 4.924-2.8.584-.338.525-.8.029-1.046-.459-.255-4.334-2.475-4.92-2.835l-3.203 3.392.004-.003zm-.803-.8l2.984-3.169-4.259-2.431S5.309 1.505 2.999.179l10.53 10.898h-.002z" />
                  </svg>
                </a>
              </div>
            </div>
            {menuItems.map((section, sectionIdx) => (
              <div key={sectionIdx} className="col-span-1">
                <h3 className="mb-4 font-bold text-foreground">{section.title}</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx} className="text-sm font-medium hover:text-primary transition-colors">
                      {link.url === "__cookie_preferences__" ? (
                        <button
                          type="button"
                          onClick={openPreferences}
                          className="p-0 bg-transparent border-0 text-right underline-offset-4 hover:underline"
                          aria-label="ניהול העדפות עוגיות"
                        >
                          {link.text}
                        </button>
                      ) : isExternalLink(link.url) ? (
                        <a
                          href={link.url}
                          target={link.url.startsWith("http") ? "_blank" : undefined}
                          rel={link.url.startsWith("http") ? "noopener noreferrer" : undefined}
                        >
                          {link.text}
                        </a>
                      ) : (
                        <Link to={link.url}>{link.text}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col gap-6 border-t pt-8">
            <div className="flex flex-col-reverse gap-6 text-sm font-medium text-muted-foreground md:flex-row md:items-center md:justify-between">
              <p className="text-center md:text-right">{copyright}</p>
              <div className="flex justify-center items-center gap-3 flex-wrap">
                <img src={visaIcon} alt="Visa" className="h-6 opacity-60 hover:opacity-100 transition-opacity" />
                <img
                  src={mastercardIcon}
                  alt="Mastercard"
                  className="h-6 opacity-60 hover:opacity-100 transition-opacity"
                />
                <img src={paypalIcon} alt="PayPal" className="h-6 opacity-60 hover:opacity-100 transition-opacity" />
                <img
                  src={amexIcon}
                  alt="American Express"
                  className="h-6 opacity-60 hover:opacity-100 transition-opacity"
                />
                <img
                  src={dinersIcon}
                  alt="Diners Club"
                  className="h-6 opacity-60 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export { Footer2 };
